import { Badge, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { format, parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportParameter } from '$features/report-hub/stability-report/stability-report.types';

import './setpoint-changes-dropdown.scss';

interface SetpointChangesDropdownProps {
  setpoints: NonNullable<ReportParameter['setpoints']>;
}

export function SetpointChangesDropdown({ setpoints }: SetpointChangesDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="stability-sidecar__changes-dropdown m-bottom-s">
      <button
        type="button"
        className="stability-sidecar__changes-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <Badge size="small" color="blue" className="stability-sidecar__changes-badge-btn">
          <span>
            {t('stabilityDetail.setpointChanges.changesCount', {
              count: setpoints.length,
            })}
          </span>
          <Icon
            name={isOpen ? 'up' : 'down'}
            size={IconSizeClasses.XXSmall}
            className="bg-gray-900"
          />
        </Badge>
      </button>
      {isOpen && (
        <div className="stability-sidecar__setpoints-popup p-m" role="list">
          <span className="font-weight-bold flex m-bottom-xs">
            {t('stabilityDetail.setpointChanges.titleWithCount', {
              count: setpoints.length,
            })}
          </span>
          {setpoints.map((sp, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- setpoints have no stable unique key
              key={i}
              className="stability-sidecar__setpoints-item"
              role="listitem"
            >
              <span className="font-size-s">
                {format(parseISO(sp.changedAt), 'dd MMM yyyy, HH:mm')}
              </span>
              <span className="font-weight-semibold font-size-s">{sp.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
